import React from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useLocation } from 'react-router-dom';
import WeeklyCell from './WeeklyCell';
import { useDashboardData } from '../../hooks/useDashboardData';
import { detectLanguageFromPathname } from '../../i18n';

import PropTypes from 'prop-types';



const WeeklyTrend = ({ summaryData, getTodaysDate, isHistorical = false, isCompact = false }) => {
    const location = useLocation();
    const isEnglish = detectLanguageFromPathname(location.pathname) === 'en';
    const { eventData } = useDashboardData();
    const isMobile = useMediaQuery('(max-width:768px)');
    const isSmallMobile = useMediaQuery('(max-width:480px)');
    
    // 指定した日付のイベント情報を取得（去年のデータの場合は今年の同日のイベントを取得）
    const getEventsForDate = (dateStr, isHistoricalData = false) => {
        if (!eventData || !Array.isArray(eventData)) return [];
        
        try {
            if (isHistoricalData) {
                // 去年のデータの場合、今年の同日のイベントを取得
                const date = new Date(dateStr);
                const currentYear = new Date().getFullYear();
                const sameMonthDay = `${currentYear}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                return eventData.filter(event => event.date === sameMonthDay) || [];
            } else {
                return eventData.filter(event => event.date === dateStr) || [];
            }
        } catch (error) {
            console.error('Error in getEventsForDate:', error, 'dateStr:', dateStr);
            return [];
        }
    };
    
    // 今日から前後を含む 4 週間 (28日) の 7×4 グリッドに調整する関数。
    // 曜日に依らず「先のデータ量」が概ね一定 (>=14 日) になるように、
    // 前週 (= 1週間前の日曜) を起点として 4 週分を表示する。
    const adjustToFourWeekGrid = (data, referenceDate) => {
        if (!data || data.length === 0) return [];

        const today = new Date(referenceDate);

        // 今日が含まれる週の前週日曜日を起点とする
        const todayDayOfWeek = today.getDay();
        const startDate = new Date(today);
        startDate.setDate(today.getDate() - todayDayOfWeek - 7);

        const grid = [];
        for (let i = 0; i < 28; i++) {
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + i);
            const dateStr = currentDate.toISOString().split('T')[0];

            const dayData = data.find(d => d.date === dateStr);

            if (dayData) {
                grid.push({
                    ...dayData,
                    is_today: dateStr === referenceDate,
                });
            } else {
                grid.push({
                    date: dateStr,
                    congestion_level: 0,
                    is_empty: true,
                    weather_info: null,
                    is_today: dateStr === referenceDate,
                    is_future: currentDate > today,
                });
            }
        }

        return grid;
    };
    
    // バックエンドから送られてくるデータ構造に合わせて修正
    const recentWeekData = summaryData?.data?.recent_week_daily_summary || [];
    const historicalData = summaryData?.data?.historical_daily_summary || [];
    
    // 詳細データから取得する場合の予備対応
    const extendedWeekData = summaryData?.data?.extended_week?.daily_data || [];
    const historicalDetailData = summaryData?.data?.historical_comparison?.daily_data || [];
        
    const title = isHistorical 
        ? (isEnglish ? 'Last year' : '去年') : (isEnglish ? 'This year' : '今年');
    
    // 表示するデータを決定（フォールバック対応）
    let displayData = [];
    if (isHistorical) {
        if (!summaryData?.data?.historical_data_available) {
            return (
                <Box sx={{ mb: 2 }}>
                    <Typography 
                        variant={isMobile ? "subtitle2" : "h6"} 
                        gutterBottom 
                        sx={{ 
                            fontWeight: 'bold', 
                            mb: 0.3,
                            color: 'text.primary'
                        }}
                    >
                        {title}
                    </Typography>
                    
                    <Box sx={{ 
                        border: '1px solid #ddd', 
                        borderRadius: '8px', 
                        overflow: 'hidden',
                        backgroundColor: '#fff',
                        p: 2,
                        textAlign: 'center'
                    }}>
                        <Typography variant="body2" color="text.secondary">
                            {isEnglish ? 'Last year data is not available' : '去年のデータは利用できません'}
                        </Typography>
                    </Box>
                </Box>
            );
        }
        
        displayData = historicalData.length > 0 ? historicalData : historicalDetailData;
    } else {
        if (recentWeekData.length > 0) {
            displayData = recentWeekData;
        } else if (extendedWeekData.length > 0) {
            displayData = extendedWeekData.map(day => ({
                ...day,
                is_future: day.is_future || false
            }));
        }
    }
    
    // 4週間グリッドに調整（曜日に依らず常に同じ未来日数を表示）
    const todaysDate = getTodaysDate();
    // 去年データの場合は去年の今日を基準にする
    const referenceDate = isHistorical 
        ? new Date(new Date(todaysDate).setFullYear(new Date(todaysDate).getFullYear() - 1)).toISOString().split('T')[0]
        : todaysDate;
    const adjustedData = adjustToFourWeekGrid(displayData, referenceDate);
    
    // 調整後のデータを使用
    const dailyData = adjustedData;
        
    if (dailyData.length === 0) {
        return (
            <Box>
                <Typography 
                    variant={isMobile ? "subtitle2" : "h6"} 
                    gutterBottom 
                    sx={{ 
                        fontWeight: 'bold', 
                        mb: 0.5,
                        color: 'text.primary'
                    }}
                >
                    {title}
                </Typography>
                
                <Box sx={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '8px', 
                    overflow: 'hidden',
                    backgroundColor: '#fff',
                    p: 2,
                    textAlign: 'center'
                }}>
                    <Typography variant="body2" color="text.secondary">
                        {isEnglish ? 'Recent weekly data is not available yet' : '最近の週間データはまだ利用できません'}
                    </Typography>
                </Box>
            </Box>
        );
    }

    // 曜日ごとにグループ化してセクション表示（7×2グリッド）
    const groupedByWeek = [];
    for (let i = 0; i < dailyData.length; i += 7) {
        groupedByWeek.push(dailyData.slice(i, i + 7));
    }

    return (
        <Box>
            {!isCompact && (
                <Typography 
                    variant={isMobile ? "subtitle2" : "h6"} 
                    gutterBottom 
                    sx={{ 
                        fontWeight: 'bold', 
                        mb: 0.5,
                        color: 'text.primary'
                    }}
                >
                    {title}
                </Typography>
            )}
            <Box sx={{ 
                maxWidth: isCompact ? '100%' : '800px', 
                margin: '0 auto', 
                border: '1px solid #ddd', 
                borderRadius: '8px', 
                overflow: 'hidden',
                width: '100%',
                position: 'relative'
            }}>
                {/* 曜日のヘッダー（日曜日始まり固定） */}
                <Box sx={{ 
                    display: 'flex', 
                    backgroundColor: '#f5f5f5', 
                    borderBottom: '1px solid #ddd'
                }}>
                    {(isEnglish
                        ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
                        : ['日', '月', '火', '水', '木', '金', '土']).map((dayName, index) => (
                        <Box key={index} sx={{ 
                            flex: 1, 
                            textAlign: 'center', 
                            padding: isCompact ? '1px' : (isMobile ? '1px' : '4px')
                        }}>
                            <Typography 
                                variant={isSmallMobile ? "bodyS" : "bodyM"} 
                                fontWeight="bold"
                                sx={{ 
                                    fontSize: isCompact 
                                        ? (isMobile ? '10px' : '12px') 
                                        : (isMobile ? '12px' : '14px')
                                }}
                            >
                                {dayName}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* 週単位でデータを表示（Calendar.jsスタイル） */}
                {groupedByWeek.map((week, rowIndex) => (
                    <Box key={rowIndex} sx={{ display: 'flex' }}>
                        {week.map((day, colIndex) => {
                            return (
                                <Box
                                    key={colIndex}
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderRight: colIndex !== 6 ? '1px solid #ddd' : undefined,
                                        borderBottom: rowIndex !== groupedByWeek.length - 1 ? '1px solid #ddd' : undefined,
                                        position: 'relative',
                                        cursor: 'default',
                                        height: isCompact 
                                            ? (isMobile ? (isSmallMobile ? '45px' : '50px') : '70px')
                                            : (isMobile ? (isSmallMobile ? '50px' : '55px') : '90px'),
                                        minWidth: isMobile ? (isSmallMobile ? '30px' : '40px') : '50px',
                                    }}
                                >
                                    <WeeklyCell 
                                        day={day} 
                                        isCurrentYear={!isHistorical}
                                        getTodaysDate={getTodaysDate}
                                        isCompactMode={true}
                                        isUltraCompact={isCompact}
                                        events={getEventsForDate(day.date, isHistorical)}
                                        isEnglish={isEnglish}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

WeeklyTrend.propTypes = {
    summaryData: PropTypes.shape({
        data: PropTypes.shape({
            recent_week_daily_summary: PropTypes.arrayOf(PropTypes.object),
            historical_daily_summary: PropTypes.arrayOf(PropTypes.object),
            historical_data_available: PropTypes.bool,
            historical_reference_date: PropTypes.string,
            weeks_count: PropTypes.number,
            extended_week: PropTypes.shape({
                daily_data: PropTypes.arrayOf(PropTypes.object)
            }),
            historical_comparison: PropTypes.shape({
                daily_data: PropTypes.arrayOf(PropTypes.object)
            })
        })
    }),
    getTodaysDate: PropTypes.func.isRequired,
    isHistorical: PropTypes.bool,
    isCompact: PropTypes.bool
};

export default WeeklyTrend;
